// Findings tab — severity filter + list. Clicking a finding opens the detail.

import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Empty } from "@/components/ui/Empty";
import { Loading } from "@/components/ui/Loading";
import { SeverityBadge } from "@/components/ui/SeverityBadge";
import { useEngagementFindings } from "@/hooks/useEngagementDetail";
import type { Finding } from "@/types/engagement-detail";
import { FindingDetailDialog } from "./FindingDetailDialog";
import { useDict, fmt } from "@/i18n/LocaleProvider";

const FILTERS = ["all", "critical", "high", "medium", "low", "informational"] as const;
type Filter = (typeof FILTERS)[number];

const STATUS_COLORS: Record<string, { c: string; b: string }> = {
  needs_review: { c: "rgb(var(--color-warning))", b: "rgb(var(--color-warning-bg))" },
  confirmed: { c: "rgb(var(--color-danger))", b: "rgb(var(--color-danger-bg))" },
  false_positive: { c: "rgb(var(--color-muted))", b: "rgb(var(--color-bg3))" },
  needs_retest: { c: "rgb(var(--color-brand))", b: "rgb(var(--color-brand-bg))" },
  retest_pass: { c: "rgb(var(--color-success))", b: "rgb(var(--color-success-bg))" },
  retest_fail: { c: "rgb(var(--color-danger))", b: "rgb(var(--color-danger-bg))" },
  informational: { c: "rgb(var(--color-muted))", b: "rgb(var(--color-bg3))" },
};

function severityOf(f: Finding): string {
  return (f.severity_final || f.severity_suggested || "informational") as string;
}

export function FindingsTab({ engagementId }: { engagementId: string }) {
  const dd = useDict();
  const t = dd.app.findings;
  const sev = dd.app.severities;
  const { data, isLoading } = useEngagementFindings(engagementId);
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<Finding | null>(null);

  if (isLoading) return <Loading text={t.loading} />;

  const findings = data ?? [];

  if (selected) {
    return (
      <FindingDetailDialog
        engagementId={engagementId}
        finding={selected}
        onBack={() => setSelected(null)}
      />
    );
  }

  const filtered =
    filter === "all"
      ? findings
      : findings.filter((f) => severityOf(f) === filter);

  return (
    <div>
      <div className="flex gap-1 mb-3 flex-wrap">
        {FILTERS.map((f) => {
          const count =
            f === "all"
              ? findings.length
              : findings.filter((x) => severityOf(x) === f).length;
          return (
            <Button
              key={f}
              small
              variant={filter === f ? "primary" : "default"}
              onClick={() => setFilter(f)}
            >
              {f === "all" ? fmt(t.all, { n: count }) : `${sev[f as keyof typeof sev]} (${count})`}
            </Button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <Empty text={t.noMatch} />
      ) : (
        <div className="flex flex-col gap-[6px]">
          {filtered.map((f) => {
            const sc = STATUS_COLORS[f.status] ?? STATUS_COLORS.informational;
            return (
              <Card
                key={f.id}
                onClick={() => setSelected(f)}
                className="!px-4 !py-3"
              >
                {/* Mobile: title first, then a single wrap row of all
                    badges. Desktop: original single-row layout with the
                    title in the middle filling available space. */}
                <div className="md:hidden">
                  <div className="font-mono text-[13px] text-content break-words mb-2">
                    <span className="text-dim mr-[6px]">#{f.display_number ?? "—"}</span>
                    {f.title}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <SeverityBadge severity={severityOf(f)} />
                    {f.cvss_score != null && (
                      <Badge color={f.cvss_score >= 7 ? "rgb(var(--color-danger))" : f.cvss_score >= 4 ? "rgb(var(--color-warning))" : "rgb(var(--color-success))"} bg="rgb(var(--color-bg3))">
                        CVSS {f.cvss_score.toFixed(1)}
                      </Badge>
                    )}
                    {f.risk_score != null && (
                      <Badge color={f.risk_score >= 70 ? "rgb(var(--color-danger))" : f.risk_score >= 40 ? "rgb(var(--color-warning))" : "rgb(var(--color-success))"} bg="rgb(var(--color-bg3))">
                        R:{f.risk_score}
                      </Badge>
                    )}
                    <Badge color={sc.c} bg={sc.b}>
                      {t.statuses[f.status as keyof typeof t.statuses] ?? f.status}
                    </Badge>
                    {f.ai_enriched && (
                      <Badge color="rgb(var(--color-accent-bright))" bg="rgb(var(--color-accent-bg))">AI</Badge>
                    )}
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2">
                  <SeverityBadge severity={severityOf(f)} />
                  {f.cvss_score != null && (
                    <Badge color={f.cvss_score >= 7 ? "rgb(var(--color-danger))" : f.cvss_score >= 4 ? "rgb(var(--color-warning))" : "rgb(var(--color-success))"} bg="rgb(var(--color-bg3))">
                      CVSS {f.cvss_score.toFixed(1)}
                    </Badge>
                  )}
                  {f.risk_score != null && (
                    <Badge color={f.risk_score >= 70 ? "rgb(var(--color-danger))" : f.risk_score >= 40 ? "rgb(var(--color-warning))" : "rgb(var(--color-success))"} bg="rgb(var(--color-bg3))">
                      R:{f.risk_score}
                    </Badge>
                  )}
                  <span className="font-mono text-[13px] text-content flex-1">
                    <span className="text-dim mr-[6px]">#{f.display_number ?? "—"}</span>
                    {f.title}
                  </span>
                  <Badge color={sc.c} bg={sc.b}>
                    {t.statuses[f.status as keyof typeof t.statuses] ?? f.status}
                  </Badge>
                  {f.ai_enriched && (
                    <Badge color="rgb(var(--color-accent-bright))" bg="rgb(var(--color-accent-bg))">AI</Badge>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
